import { Card, Checkbox, Form, Row, Input, Button, Space, Divider, Modal } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { UserOutlined } from "@ant-design/icons";

import React, { useCallback, useEffect, useState } from "react";
import { CookiesProvider, useCookies } from "react-cookie";

import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAsyncAxios, utilAxiosWithAppToken } from "../utils/customAxios";
import { useAuthDispatch } from "../provider/AuthProvider";
import { useAlert } from "../provider/AlertProvider";

import * as Strings from "../commons/string";
import * as Common from "../commons/common";
import * as Request from "../commons/request";
import * as Utils from "../utils/utils";

import PageContainer from "../layout/PageContainer";
import InputPassword from "../components/InputPassword";

import styles from "./Login.module.css";

function Login() {
    const [form] = Form.useForm();

    const dispatch = useAuthDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const alert = useAlert();

    const [user, setUser] = useState<string>("");
    const [pwd, setPwd] = useState<string>("");

    const [checkSaveEmail, setCheckSaveEmail] = useState<boolean>(false);
    const [checkAutoLogin, setCheckAutoLogin] = useState<boolean>(true);

    useEffect(() => {
        const savedEmail = Utils.getLocalStorage(Common.CONTEXT_SAVED_EMAIL);
        if (savedEmail && savedEmail.length > 0) {
            setCheckSaveEmail(true);
            form.setFieldValue("username", savedEmail);
            setUser(savedEmail);
        } else setCheckSaveEmail(false);
    }, []);

    useEffect(() => {
        const state = location.state;

        console.log(location.state);

        if (state?.error) {
            alert.setAlert("", "로그인 실패", state?.error);
        }
    }, [location]);

    const validateUser = useCallback((_: any, value: string) => {
        if (!value) {
            return Promise.reject(new Error(Strings.msg_email_must));
        }
        return Promise.resolve();
    }, []);

    const validatePwd = useCallback((_: any, value: string) => {
        const pwd_regExp = Common.PASSWORD_REGEXP;

        if (!value) {
            return Promise.reject(new Error(Strings.msg_pwd_must));
        }
        if (!pwd_regExp.test(value)) {
            return Promise.reject(new Error(Strings.msg_pwd_rule));
        }
        return Promise.resolve();
    }, []);

    const onChangedSaveEmail = (checked: boolean) => {
        console.log(checked);
        setCheckSaveEmail(checked);
    };

    const onChangedAutoLogin = (checked: boolean) => {
        console.log(checked);
        setCheckAutoLogin(checked);
    };

    const requestLogin = async (url: string, option: Record<string, string>) => {
        const response = await utilAxiosWithAppToken().post(url, option);
        return response.data;
    };

    const { loading, error: errorLogin, data: resultLogin, execute: login } = useAsyncAxios(requestLogin);

    useEffect(() => {
        if (resultLogin === null) return;

        if (
            resultLogin.user.authority !== "ROLE_ADMIN" &&
            resultLogin.user.authority !== "ROLE_MANAGER" &&
            resultLogin.user.authority !== "ROLE_RENT_MANAGER" &&
            resultLogin.user.authority !== "ROLE_B2B_MANAGER"
        ) {
            if (dispatch !== null)
                dispatch({
                    type: "LOGOUT",
                });

            alert.setAlert("", "로그인 실패", Strings.msg_admin_must);

            return;
        }

        if (dispatch !== null)
            dispatch({
                type: "LOGIN_SUCCESS",
                payload: {
                    user: resultLogin.user,
                    token: resultLogin.token,
                },
            });

        if (resultLogin.result === "ok") {
            if (checkAutoLogin) {
                Utils.setLocalStorage(Common.CONTEXT_SAVED_EMAIL, user);
                Utils.setLocalStorage(Common.CONTEXT_SAVED_PASSWORD, pwd);
            } else {
                if (checkSaveEmail) {
                    Utils.setLocalStorage(Common.CONTEXT_SAVED_EMAIL, user);
                } else {
                    Utils.setLocalStorage(Common.CONTEXT_SAVED_EMAIL, "");
                }
                Utils.setLocalStorage(Common.CONTEXT_SAVED_PASSWORD, "");
            }

            navigate(Common.PAGE_MAIN, { replace: true });
        }

        setPwd("");
    }, [resultLogin]);

    useEffect(() => {
        if (errorLogin === null) {
            return;
        }

        if (dispatch !== null) {
            dispatch({ type: "LOGIN_ERROR", error: "Login failed" });
        }

        console.log("errorLogin", errorLogin);
    }, [errorLogin]);

    const actionLogin = async () => {
        if (user.length === 0) {
            alert.setAlert("", "로그인 실패", Strings.msg_email_must);
        } else if (pwd.length === 0) {
            alert.setAlert("", "로그인 실패", Strings.msg_pwd_must);
        } else await login(Request.LOGIN_URL, JSON.stringify({ email: user, password: pwd }));
    };

    return (
        <PageContainer pageBackground={"white"}>
            <Row align="middle" justify="center">
                <div className={`${styles.loginCard} container`}>
                    <Form name="normal_login" form={form} initialValues={{ remember: true }} layout="vertical" onFinish={actionLogin}>
                        <CookiesProvider>
                            <Form.Item
                                label={<p className={styles.textInputLabel}>이메일</p>}
                                name="username"
                                rules={[
                                    {
                                        validator: (_, value) => Utils.validateEmail(true, false, value),
                                    },
                                ]}
                            >
                                <Input
                                    className={styles.loginInput}
                                    prefix={<UserOutlined className="site-form-item-icon" />}
                                    placeholder="이메일"
                                    maxLength={100}
                                    value={user}
                                    onInput={(e) => ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toLowerCase())}
                                    onChange={(e) => setUser(e.target.value)}
                                />
                            </Form.Item>
                        </CookiesProvider>
                        <Form.Item label={<p className={styles.textInputLabel}>비밀번호</p>} name="password" rules={[{ validator: validatePwd }]}>
                            <InputPassword onChange={(value) => setPwd(value)} />
                        </Form.Item>

                        <Space size={10} style={{ marginBottom: "40px" }}>
                            <Checkbox
                                checked={checkSaveEmail}
                                onChange={(e) => {
                                    onChangedSaveEmail(e.target.checked);
                                }}
                            >
                                {Strings.save_email}
                            </Checkbox>
                            <Checkbox
                                checked={checkAutoLogin}
                                onChange={(e) => {
                                    onChangedAutoLogin(e.target.checked);
                                }}
                            >
                                {Strings.auto_login}
                            </Checkbox>
                        </Space>

                        <Form.Item className={styles.loginBtnWrapper}>
                            <Button
                                className={styles.loginBtn}
                                loading={loading}
                                block
                                type="primary"
                                htmlType="submit"
                                size="large"
                                disabled={user && pwd ? false : true}
                            >
                                {!loading ? Strings.login : ""}
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </Row>
        </PageContainer>
    );
}

export default Login;
